import React, { useState, useEffect } from "react";
import { Mail, RefreshCw, CheckCircle } from "lucide-react";
import apiClient from "@/lib/apiClient";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import Header from "../../header";
import { ThemedButton, ThemedCard } from "@/components/themed";
import { useAuth } from "@/components/AuthContext";

interface Site {
  id: number;
  name: string;
  subdomain: string;
  slug: string;
  description: string;
  icon_url: string | null;
  thumbnail_url: string | null;
  privacy_url: string | null;
  terms_url: string | null;
  trade_law_url: string | null;
  created_at: string;
  identity_verified: boolean;
  antique_dealer_verified: boolean;
  delivery_count: number;
}

interface EmailVerificationPendingPageProps {
  siteData?: Site;
}

const EmailVerificationPendingPage: React.FC<EmailVerificationPendingPageProps> = ({ siteData }) => {
  const { currentUser } = useAuth();
  const [email, setEmail] = useState<string>("");
  const [isSending, setIsSending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    // メール認証済みユーザーはトップにリダイレクト
    if (currentUser?.email_verified) {
      window.location.href = "/";
      return;
    }

    // localStorage から登録時のメールアドレスを取得
    const storedEmail = localStorage.getItem("pendingVerificationEmail");
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, [currentUser]);

  const handleResendEmail = async () => {
    if (!email) {
      toast.error("メールアドレスが見つかりません。再度会員登録してください。");
      return;
    }

    setIsSending(true);
    setResendSuccess(false);
    try {
      await apiClient.post("/customer/email_verifications", { email });
      setResendSuccess(true);
      toast.success("確認メールを再送信しました。メールをご確認ください。");
    } catch (error: any) {
      toast.error(error.response?.data?.error || "確認メールの送信に失敗しました");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>メール認証待ち | {siteData?.name || "ショップ"}</title>
        <meta property="og:title" content={`メール認証待ち | ${siteData?.name || "ショップ"}`} />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <Header siteData={siteData} />

        <section className="flex justify-center py-12 px-4 sm:px-6 lg:px-8 pt-24">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              {/* メール + チェックマーク イラスト */}
              <div className="relative w-24 h-24 mx-auto mb-6">
                {/* メインのメールアイコン */}
                <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center shadow-lg">
                  <Mail className="w-10 h-10 text-blue-500" />
                </div>
                {/* チェックマークバッジ */}
                <div className="absolute -bottom-1 -right-1 w-10 h-10 bg-green-400 rounded-full flex items-center justify-center shadow-md border-4 border-white">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                {/* 装飾のドット */}
                <div className="absolute top-0 -right-2 w-3 h-3 bg-yellow-300 rounded-full"></div>
                <div className="absolute -top-1 left-2 w-2 h-2 bg-pink-300 rounded-full"></div>
                <div className="absolute bottom-4 -left-3 w-2 h-2 bg-blue-300 rounded-full"></div>
              </div>
              <h2 className="text-2xl [font-weight:var(--theme-font-weight)] tracking-tight text-theme-bg-text">
                メール認証が必要です
              </h2>
              <p className="mt-2 text-sm [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">
                サービスをご利用いただくにはメール認証が必要です
              </p>
            </div>

            <ThemedCard className="p-8">
              <div className="space-y-6">
                {email && (
                  <div className="text-center">
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 mb-2">
                      送信先
                    </p>
                    <p className="text-base [font-weight:var(--theme-font-weight)] text-black break-all">
                      {email}
                    </p>
                  </div>
                )}

                <div className="bg-gray-50 border border-gray-200 rounded-theme p-4">
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-700 leading-relaxed">
                    メールに記載されたリンクをクリックして認証を完了してください。
                    認証完了後、自動的にログインされます。
                  </p>
                </div>

                {resendSuccess && (
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-theme p-4">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-green-700">
                      確認メールを再送信しました
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  <ThemedButton
                    type="button"
                    variant="secondary"
                    className="w-full py-3"
                    onClick={handleResendEmail}
                    disabled={isSending}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isSending ? "animate-spin" : ""}`} />
                    {isSending ? "送信中..." : "確認メールを再送信"}
                  </ThemedButton>

                  <p className="text-xs text-gray-500 text-center">
                    メールが届かない場合は、迷惑メールフォルダをご確認ください
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <a
                    href="/signup"
                    className="block text-center text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 hover:text-black underline underline-offset-2"
                  >
                    別のメールアドレスで登録する
                  </a>
                </div>
              </div>
            </ThemedCard>
          </div>
        </section>
      </div>
    </>
  );
};

export default EmailVerificationPendingPage;
